import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "../Announcement/Announcement.scss";
import { AppContext } from "../../../context/AppContext";
import api from "../../../api.js";
import { io } from "socket.io-client";
import { Calendar } from "primereact/calendar";
import Loader from "../../../components/Loader/Loader.jsx";

const StudentsFeedbackPage = () => {
  const { backendUrl } = useContext(AppContext);
  const [allStudentsFeedback, setAllStudentsFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [scrollHeight, setScrollHeight] = useState("425px");
  let errorShown = false;

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerHeight;
      if (screenWidth >= 900) {
        setScrollHeight("650px");
      } else {
        setScrollHeight("440px");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function fetchAllStudentsFeedback() {
    setLoading(true);
    try {
      const { data } = await api.get(
        `${backendUrl}/api/auth/getFeedbacksByType?type=Student`
      );
      const sortedData = [...data.data].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setAllStudentsFeedback(sortedData);
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const newSocket = io(backendUrl, {
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ["websocket"],
    });
    setSocket(newSocket);
    return () => {
      newSocket.disconnect();
    };
  }, [backendUrl]);

  useEffect(() => {
    if (!socket) return;
    fetchAllStudentsFeedback();
    socket.on("update_student_feedbacks", (newFeedback) => {
      setAllStudentsFeedback((prev) => {
        if (!prev.some((f) => f.id === newFeedback.id)) {
          return [newFeedback, ...prev].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
        }
        return prev;
      });
      toast.info("📩 New student feedback received!");
    });
    return () => {
      socket.off("update_student_feedbacks");
      socket.disconnect();
    };
  }, [socket]);

  const showConfirmDialog = (rowData) => {
    confirmDialog({
      message: (
        <div className="custom-dialog-content">
          <div className="iconParent">
            <i className="pi pi-times"></i>
          </div>
          <div className="contentParent">
            <h2>Are you sure?</h2>
            <div className="contentSubParent">
              <p>
                Delete feedback from {rowData.name} ({rowData.department})?
              </p>
              <p>Feedback: {rowData.feedback.substring(0, 50)}...</p>
              <p className="warningText">This action cannot be undone.</p>
            </div>
          </div>
        </div>
      ),
      header: "",
      icon: null,
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger custom-delete-btn",
      rejectClassName: "p-button-secondary custom-cancel-btn",
      accept: () => {
        handleDeleteClick(rowData);
      },
    });
  };

  async function handleDeleteClick(rowData) {
    setIsSubmitting(true);
    try {
      const { data } = await api.delete(
        `${backendUrl}/api/auth/deleteAFeedback/${rowData.id}`
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllStudentsFeedback();
      }
    } catch (error) {
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const formatDateForAPI = (date) => {
    const d = new Date(date);
    const offset = d.getTimezoneOffset() * 60000;
    const localDate = new Date(d.getTime() - offset);
    return localDate.toISOString().split("T")[0];
  };

  const handleDownloadStudentFeedbacks = async () => {
    setIsSubmitting(true);
    if (!startDate || !endDate) {
      toast.warn("Please select both start and end dates.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    try {
      setLoading(true);
      const formattedStart = formatDateForAPI(startDate);
      const formattedEnd = formatDateForAPI(endDate);
      const response = await api.get(
        `${backendUrl}/api/auth/exportFeedbacksExcel`,
        {
          params: {
            startDate: formattedStart,
            endDate: formattedEnd,
            type: "Student",
          },
          responseType: "blob",
        }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Student_Feedbacks_${formattedStart}_to_${formattedEnd}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download Excel file.");
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <section className="adminAnnoucementMainContainer">
      <Loader visible={isSubmitting} />

      <div className="headerParent">
        <h2>Welcome to Admin Students Feedback Page</h2>
      </div>

      <div className="dateAndDownloadBtnContainer">
        <div className="dateFieldParent">
          <label htmlFor="startDate" className="dateLabelNames">
            Start Date
          </label>
          <Calendar
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.value)}
            dateFormat="dd-mm-yy"
            showIcon
            inputStyle={{ padding: "0.6rem 0.8rem", width: "175px" }}
          />
        </div>
        <div className="dateFieldParent">
          <label htmlFor="endDate" className="dateLabelNames">
            End Date
          </label>
          <Calendar
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.value)}
            dateFormat="dd-mm-yy"
            showIcon
            inputStyle={{ padding: "0.6rem 0.8rem", width: "175px" }}
          />
        </div>
        <div className="downloadExcelBtnParent">
          <Button
            label=" Download Excel"
            icon="pi pi-download"
            className="p-button-success"
            onClick={handleDownloadStudentFeedbacks}
            disabled={loading}
            style={{
              padding: "0.6rem 1.5rem",
              fontWeight: "600",
              fontSize: "1rem",
              fontFamily: "var(--font-paragraph)",
              gap: "0.5rem",
            }}
          />
        </div>
      </div>

      <article className="tableWrapper">
        <div className="scrollableTableContainer">
          <DataTable
            value={allStudentsFeedback}
            showGridlines
            stripedRows
            className="table"
            emptyMessage="No student feedback available"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            scrollable
            scrollHeight={scrollHeight}
            loading={loading}
          >
            <Column
              header="S.No"
              body={(_, options) => options.rowIndex + 1}
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              field="name"
              header="Name"
              className="tableData"
              style={{ minWidth: "220px", maxWidth: "325px" }}
            />
            <Column
              field="department"
              header="Department"
              className="tableData"
              style={{ minWidth: "190px", maxWidth: "325px" }}
            />
            <Column
              field="email"
              header="Email"
              className="tableData"
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="feedback"
              header="Feedback"
              className="tableData"
              style={{ minWidth: "300px", maxWidth: "325px" }}
            />
            <Column
              field="createdAt"
              header="Created At"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
              className="tableData"
            />
            <Column
              header="Action"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
              body={(rowData) => (
                <div className="action-buttons">
                  <Button
                    label="Delete"
                    severity="danger"
                    className="actionBtns"
                    onClick={() => showConfirmDialog(rowData)}
                  />
                </div>
              )}
              className="tableData"
            />
          </DataTable>
        </div>
      </article>
    </section>
  );
};

export default StudentsFeedbackPage;
